import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { UserContext } from '../context/UserContext';
import Modal from '../components/modal';
import AuthAPI from '../api/auth/AuthAPI';

function IdleTimerInner({ children, logout }) {
    const { userIP, secureIpAddresses } = useContext(UserContext);

    const readPosInt = v => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
    };

    // env (minuten en seconden)
    const baseIdleMin   = readPosInt(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES)         ?? 5;
    const baseWarnSec   = readPosInt(process.env.REACT_APP_IDLE_WARNING_SECONDS)         ?? 60;
    const secureIdleMin = readPosInt(process.env.REACT_APP_SECURED_IDLE_TIMEOUT_MINUTES) ?? 350; // 5u50
    const secureWarnSec = readPosInt(process.env.REACT_APP_SECURED_IDLE_WARNING_SECONDS) ?? 300;

    // Zoek het bijpassende secure-record (met duration_idle_minutes)
    const matchedSecureEntry = useMemo(() => {
        return secureIpAddresses?.find(i => i.ipAddress === userIP) ?? null;
    }, [secureIpAddresses, userIP]);

    const isSecureIpAddress = !!matchedSecureEntry;

    // Als er een match is en duration_idle_minutes is ingevuld/valide → gebruik die,
    // anders fallback naar secureIdleMin uit env
    const perIpSecureIdleMin = readPosInt(matchedSecureEntry?.durationIdleMinutes);
    const effectiveSecureIdleMin = perIpSecureIdleMin > 0 ? perIpSecureIdleMin : secureIdleMin;
    // kies effectieve waarden o.b.v. secure/non-secure
    const IDLE_MIN = isSecureIpAddress ? effectiveSecureIdleMin : baseIdleMin;
    const WARN_SECONDS_RAW = isSecureIpAddress ? secureWarnSec : baseWarnSec;
    const WARN_SECONDS = Math.min(WARN_SECONDS_RAW, IDLE_MIN * 60 - 1); // nooit ≥ timeout

    const [showWarn, setShowWarn] = useState(false);
    const [, forceTick] = useState(0); // force re-render voor countdown

    // tijdstempel van laatste activiteit (wall-clock)
    // ref i.p.v. state voor wall-clock (geen effect churn)
    const lastActivityRef = useRef(Date.now());

    const handleOnPrompt = useCallback(() => {
        setShowWarn(true);
    }, []);

    const handleOnIdle = useCallback(() => {
        setShowWarn(false);
        logout('idle_timeout');
    }, [logout]);

    // const handleOnActive = useCallback(() => {
    //     setShowWarn(false);
    // }, []);

    const { getRemainingTime, reset, activate } = useIdleTimer({
        name: 'custinfo-idle-timer',
        timeout: IDLE_MIN * 60 * 1000,
        promptBeforeIdle: WARN_SECONDS * 1000,
        onPrompt: handleOnPrompt,
        onIdle: handleOnIdle,
        // onActive: handleOnActive,
        events: ['keydown', 'mousedown', 'touchstart'],
        crossTab: true,
        eventsThrottle: 200,
        stopOnIdle: false,
        startOnMount: true, // wrapper heeft dit al afgevangen, dus hier altijd true
        onAction: () => {
            // elke echte gebruikersactie reset onze wall-clock timestamp
            lastActivityRef.current = Date.now();
        },
        // onAction: (event) => {
        //     console.log('onAction event:', event?.type);
        //     lastActivityRef.current = Date.now();
        // },
    });

    // countdown bij zichtbare warning
    useEffect(() => {
        if (!showWarn) return;
        const i = setInterval(() => forceTick(t => t + 1), 1000);
        return () => clearInterval(i);
    }, [showWarn]);

    // Tijdelijk loggen van matched secure entry
    // useEffect(() => {
    //     if (matchedSecureEntry){
    //         console.log('matchedSecureEntry: ', matchedSecureEntry);
    //         console.log('perIpSecureIdleMin: ', perIpSecureIdleMin);
    //         console.log('effectiveSecureIdleMin: ', effectiveSecureIdleMin);
    //         console.log('baseIdleMin: ', baseIdleMin);
    //         console.log('baseWarnSec: ', baseWarnSec);
    //         console.log('secureIdleMin: ', secureIdleMin);
    //         console.log('secureWarnSec: ', secureWarnSec);
    //     }
    // }, [matchedSecureEntry]);

    // extra wall-clock controle — vangt lange timeouts op (browser timer throttling)
    useEffect(() => {
        // zet check interval in ms
        const CHECK_INTERVAL_MS = 5 * 60 * 1000; // productie
        // const CHECK_INTERVAL_MS = 10 * 1000; // tijdelijk bij test

        const intervalId = setInterval(() => {
            const now = Date.now();
            const idleMs = now - lastActivityRef.current;

            const totalIdleLimitMs = IDLE_MIN * 60 * 1000;
            const warnThresholdMs = Math.max(0, totalIdleLimitMs - WARN_SECONDS * 1000);

            // nog geen warning in beeld, maar idle-duur is al voorbij warn-drempel
            if (!showWarn && idleMs >= warnThresholdMs && idleMs < totalIdleLimitMs) {
                setShowWarn(true);
            }

            // idle-duur voorbij totale limiet → uitloggen
            if (idleMs >= totalIdleLimitMs) {
                handleOnIdle();
            }
        }, CHECK_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [IDLE_MIN, WARN_SECONDS, showWarn, handleOnIdle]);

    const secondsLeft = Math.max(0, Math.ceil(getRemainingTime() / 1000));

    return (
        <>
            {showWarn && (
                <Modal
                    title={'Sessie wordt beëindigd vanwege inactiviteit'}
                    closeModal={() => { setShowWarn(false); logout('manual'); }}
                    closeModalText={'Log nu uit'}
                    confirmAction={async () => {
                        try {
                            await AuthAPI.ping(); // ping + sessie refresh
                        } catch (e) {
                            logout('token_expired');
                            return;
                        }

                        lastActivityRef.current = Date.now();
                        setShowWarn(false);
                        reset();
                        activate();
                    }}
                    confirmText={'Blijf ingelogd'}
                >
                    <p>Je wordt zo automatisch uitgelogd…</p>
                    <p>Nog {secondsLeft} sec</p>
                </Modal>
            )}
            {children}
        </>
    );
}

export default IdleTimerInner;
